/* Runtime StatusBar initialiser — forces the iOS/Android status bar to
   OVERLAY the WebView so the React header can paint behind it.

   Why this exists:
   capacitor.config.json controls the default behaviour, but those values
   are only honoured at install time on iOS. Once a user has the app
   installed, only a runtime call to `StatusBar.setOverlaysWebView()`
   (Android) or `StatusBar.setStyle()` (iOS) actually changes the
   behaviour. This file is imported once at app boot from index.js.

   The rule for LEXIQ: the topic bar ALWAYS owns the dead-zone (notch /
   status-bar area). Never let the native OS paint a separate band above
   our React header. Each screen's fixed header is responsible for the
   visual look of the dead-zone via `env(safe-area-inset-top)` padding
   on its top edge.
*/
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

let initialised = false;

export async function initStatusBarOverlay() {
  if (initialised) return;
  initialised = true;
  if (!Capacitor.isNativePlatform()) return;
  try {
    // Android: explicitly force overlay mode at runtime.
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
    // Both platforms: dark icons on light backgrounds (since our headers
    // default to white). Pages that opt into dark headers can call
    // `StatusBar.setStyle({ style: Style.Light })` themselves.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {
    // Silently swallow — status-bar control is non-critical and may
    // throw on the simulator before the plugin handshake finishes.
  }
}

/**
 * Per-screen helper. Each page that wants control over the dead-zone
 * appearance calls this in a useEffect. The visual look itself is
 * driven by CSS / React (the fixed header background), this just
 * makes sure the status-bar text colour is readable over it.
 */
export async function setStatusBarStyle({ light = false } = {}) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await StatusBar.setStyle({ style: light ? Style.Light : Style.Dark });
  } catch {
    /* no-op */
  }
}
